import { useRef, useEffect } from "react";

const SPEAKER_COLORS = {
  "Speaker 0": { dot: "bg-cyan-400", text: "text-cyan-400", border: "border-l-cyan-500" },
  "Speaker 1": { dot: "bg-amber-400", text: "text-amber-400", border: "border-l-amber-500" },
  "Speaker 2": { dot: "bg-green-400", text: "text-green-400", border: "border-l-green-500" },
  "Speaker 3": { dot: "bg-red-400", text: "text-red-400", border: "border-l-red-500" },
  "Speaker 4": { dot: "bg-purple-400", text: "text-purple-400", border: "border-l-purple-500" },
};

function getSpeakerStyle(speaker) {
  return SPEAKER_COLORS[speaker] || { dot: "bg-gray-400", text: "text-gray-400", border: "border-l-gray-500" };
}

export default function LiveTranscript({ segments, partialText, isRecording }) {
  const scrollRef = useRef(null);
  const autoScroll = useRef(true);

  useEffect(() => {
    if (autoScroll.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [segments, partialText]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    autoScroll.current = scrollHeight - scrollTop - clientHeight < 40;
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-2 rounded-full bg-green-400" />
        <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Live Transcript</h3>
        {isRecording && <span className="text-[10px] text-cyan-400 animate-pulse">● REC</span>}
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto space-y-1 pr-1 scrollbar-thin scrollbar-thumb-gray-700"
        style={{ maxHeight: "calc(100vh - 280px)", minHeight: 200 }}
      >
        {segments.length === 0 && !isRecording && (
          <div className="flex items-center justify-center h-full text-gray-600 text-sm">
            <p>Waiting for audio...</p>
          </div>
        )}

        {segments.map((seg, i) => {
          const ss = getSpeakerStyle(seg.speaker);
          return (
            <div key={seg.id || i} className={`group border-l-2 ${ss.border} pl-3 py-1 hover:bg-gray-800/30 transition-colors rounded-r`}>
              {seg.speaker && (
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${ss.dot}`} />
                  <span className={`text-[10px] font-semibold ${ss.text} uppercase`}>{seg.speaker}</span>
                  <span className="text-[9px] text-gray-600 ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                    {seg.start_time != null ? `${seg.start_time.toFixed(1)}s` : ""}
                  </span>
                </div>
              )}
              <p className={`text-sm leading-relaxed ${seg.is_final ? "text-green-300" : "text-amber-300 italic"}`}>
                {seg.text}
                {seg.quality_control?.corrections_count > 0 && (
                  <span className="ml-2 text-[9px] text-cyan-500 font-medium" title={`${seg.quality_control.corrections_count} corrections made`}>
                    ✦ QC
                  </span>
                )}
              </p>
              {seg.raw_text && seg.raw_text !== seg.text && (
                <details className="mt-0.5 hidden group-hover:block">
                  <summary className="text-[9px] text-gray-600 cursor-pointer">raw</summary>
                  <p className="text-[10px] text-gray-500 italic mt-0.5">{seg.raw_text}</p>
                </details>
              )}
            </div>
          );
        })}

        {partialText && (
          <div className="border-l-2 border-l-gray-600 pl-3 py-1 animate-pulse">
            <p className="text-sm text-gray-400 italic">▌{partialText}</p>
          </div>
        )}
      </div>
    </div>
  );
}
